import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { Box, CircularProgress, IconButton, TextField } from '@mui/material';
import React from 'react';

import type { CopilotPromptSendState } from './hooks/useCopilotChatFlow';
import { addCopilotPromptInsertListener, type CopilotPromptInsertRequest } from './promptInsertion';

interface Props {
  promptDraft: string;
  setPromptDraft: React.Dispatch<React.SetStateAction<string>>;
  onSendPrompt: () => void;
  onStop?: () => void;
  sendState?: CopilotPromptSendState;
  disabled?: boolean;
}

export const CopilotPromptComposer: React.FC<React.PropsWithChildren<Props>> = ({
  promptDraft,
  setPromptDraft,
  onSendPrompt,
  onStop,
  sendState = 'idle',
  disabled = false,
}) => {
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const pendingSelectionRef = React.useRef<number | null>(null);
  const wasDisabledRef = React.useRef(disabled);
  const [shouldAnimate, setShouldAnimate] = React.useState(false);
  const isSubmitting = sendState === 'submitting';
  const isStreaming = sendState === 'streaming';
  const isBusy = isSubmitting || isStreaming;

  const handlePromptInsert = React.useCallback(
    ({ insertText, emptyPromptText, focus = true }: CopilotPromptInsertRequest) => {
      setPromptDraft((currentDraft) => {
        const nextInsertText = !currentDraft.trim() && emptyPromptText ? emptyPromptText : insertText;
        const selectionStart = inputRef.current?.selectionStart ?? currentDraft.length;
        const selectionEnd = inputRef.current?.selectionEnd ?? currentDraft.length;
        const nextDraft = currentDraft.slice(0, selectionStart) + nextInsertText + currentDraft.slice(selectionEnd);

        pendingSelectionRef.current = selectionStart + nextInsertText.length;

        window.requestAnimationFrame(() => {
          if (!inputRef.current) {
            return;
          }

          if (focus) {
            inputRef.current.focus();
          }

          if (pendingSelectionRef.current !== null) {
            inputRef.current.setSelectionRange(pendingSelectionRef.current, pendingSelectionRef.current);
            pendingSelectionRef.current = null;
          }
        });

        return nextDraft;
      });
    },
    [setPromptDraft]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (promptDraft.trim() && sendState === 'idle' && !disabled) {
        onSendPrompt();
      }
    }
  };

  React.useEffect(() => {
    if (wasDisabledRef.current && !disabled) {
      setShouldAnimate(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  React.useEffect(() => addCopilotPromptInsertListener(handlePromptInsert), [handlePromptInsert]);

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        px: 2,
        py: 2,
        backgroundColor: 'background.paper',
        opacity: disabled ? 0.4 : 1,
        transform: disabled ? 'translateY(4px)' : 'translateY(0)',
        transition: 'opacity 0.4s ease-out, transform 0.4s ease-out',
        ...(shouldAnimate && {
          animation: 'composerFadeIn 0.5s ease-out',
          '@keyframes composerFadeIn': {
            from: { opacity: 0.4, transform: 'translateY(4px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }),
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          placeholder="Ask about study cohorts, biomarker expression, or generate report sections..."
          value={promptDraft}
          onChange={(event) => setPromptDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isBusy || disabled}
          sx={{ '& .MuiInputBase-root': { pr: 5 } }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            size="small"
            aria-label={isSubmitting ? 'Sending prompt' : isStreaming ? 'Stop response' : 'Send prompt'}
            color={isStreaming ? 'secondary' : 'primary'}
            onClick={isStreaming ? onStop : onSendPrompt}
            disabled={disabled || isSubmitting || (!isStreaming && !promptDraft.trim())}
            sx={{
              bgcolor: isStreaming ? 'secondary.main' : 'primary.main',
              color: isStreaming ? 'secondary.contrastText' : 'primary.contrastText',
              '&:hover': { bgcolor: isStreaming ? 'secondary.dark' : 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              width: 32,
              height: 32,
              zIndex: 1,
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : isStreaming ? (
              <StopRoundedIcon fontSize="small" />
            ) : (
              <ArrowUpwardRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
